import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import {
  type CreatedGateway,
  createdGateway,
  type GatewayFormInput,
  PROVIDER,
  type ProviderType,
} from './schemas.js';
import { gatewayEndpoints } from './endpoints.js';

const WRITTEN_KEYS = [queryKeys.notificationGateways.lists, queryKeys.notificationGateways.details];

/**
 * Turns the flat form values into the provider-specific credential blob the
 * service encrypts. Returns `undefined` when every credential field is blank,
 * which on edit means "leave the stored credentials alone".
 */
function credentialsOf(
  values: GatewayFormInput,
  provider: ProviderType,
): Record<string, unknown> | undefined {
  switch (provider) {
    case PROVIDER.SMTP: {
      if (values.smtpHost === '') return undefined;
      return {
        host: values.smtpHost,
        port: Number(values.smtpPort),
        username: values.smtpUsername === '' ? null : values.smtpUsername,
        password: values.smtpPassword,
        secure: values.smtpSecure,
      };
    }
    case PROVIDER.SENDGRID: {
      if (values.sendgridApiKey === '') return undefined;
      return { apiKey: values.sendgridApiKey };
    }
    case PROVIDER.SES: {
      if (values.sesAccessKeyId === '') return undefined;
      return {
        region: values.sesRegion,
        accessKeyId: values.sesAccessKeyId,
        secretAccessKey: values.sesSecretAccessKey,
      };
    }
    case PROVIDER.TWILIO: {
      if (values.twilioAccountSid === '') return undefined;
      return {
        accountSid: values.twilioAccountSid,
        authToken: values.twilioAuthToken,
        fromNumber: values.twilioFromNumber === '' ? null : values.twilioFromNumber,
      };
    }
    case PROVIDER.VONAGE: {
      if (values.vonageApiKey === '') return undefined;
      return {
        apiKey: values.vonageApiKey,
        apiSecret: values.vonageApiSecret,
      };
    }
    case PROVIDER.AWS_SNS: {
      if (values.snsAccessKeyId === '') return undefined;
      return {
        senderId: values.snsSenderId,
        region: values.snsRegion,
        accessKeyId: values.snsAccessKeyId,
        secretAccessKey: values.snsSecretAccessKey,
        smsType: values.snsSmsType,
        webhookSecret: values.snsWebhookSecret === '' ? null : values.snsWebhookSecret,
      };
    }
    case PROVIDER.INFOBIP: {
      if (values.infobipApiKey === '') return undefined;
      return {
        senderId: values.infobipSenderId === '' ? null : values.infobipSenderId,
        apiBaseUrl: values.infobipApiBaseUrl,
        apiKey: values.infobipApiKey,
        webhookSecret: values.infobipWebhookSecret === '' ? null : values.infobipWebhookSecret,
      };
    }
  }
}

function priorityOf(value: string): number {
  return value === '' ? 0 : Number(value);
}

export function createGatewayPayload(values: GatewayFormInput): Record<string, unknown> {
  const credentials = credentialsOf(values, values.provider as ProviderType);
  const fromAddress = values.fromAddress === '' ? undefined : values.fromAddress;
  const fromName = values.fromName === '' ? undefined : values.fromName;

  return {
    channel: values.channel,
    provider: values.provider,
    name: values.name,
    credentials: credentials ?? {},
    ...(fromAddress === undefined ? {} : { fromAddress }),
    ...(fromName === undefined ? {} : { fromName }),
    isDefault: values.isDefault,
    isActive: values.isActive,
    priority: priorityOf(values.priority),
  };
}

export function updateGatewayPayload(values: GatewayFormInput): Record<string, unknown> {
  const credentials = credentialsOf(values, values.provider as ProviderType);
  const payload: Record<string, unknown> = {
    name: values.name,
    fromAddress: values.fromAddress === '' ? null : values.fromAddress,
    fromName: values.fromName === '' ? null : values.fromName,
    isDefault: values.isDefault,
    isActive: values.isActive,
    priority: priorityOf(values.priority),
  };

  if (credentials !== undefined) payload.credentials = credentials;

  return payload;
}

export function useCreateGateway(): UseMutationResult<CreatedGateway, unknown, GatewayFormInput> {
  return useApiMutation<GatewayFormInput, CreatedGateway>({
    mutationFn: async (api, input) => {
      const response = await api.post<unknown>(
        gatewayEndpoints.create,
        createGatewayPayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdGateway, response, gatewayEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateGatewayVariables {
  readonly gatewayId: string;
  readonly values: GatewayFormInput;
}

export function useUpdateGateway(): UseMutationResult<unknown, unknown, UpdateGatewayVariables> {
  return useApiMutation<UpdateGatewayVariables, unknown>({
    mutationFn: async (api, { gatewayId, values }) => {
      await api.put<unknown>(gatewayEndpoints.update(gatewayId), updateGatewayPayload(values));
    },
    invalidate: WRITTEN_KEYS,
  });
}

export function useSoftDeleteGateway(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, gatewayId) => {
      await api.delete<unknown>(gatewayEndpoints.delete(gatewayId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
